import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist";

import { combineReducers, configureStore } from "@reduxjs/toolkit";

import localforage from "localforage";

import { analysisJobReducer, resetAnalysisJob } from "./slice/analysisJob";
import {
  analysisSummaryReducer,
  resetAnalysisSummaryState
} from "./slice/analysisSummary";
import { authReducer, resetAuth } from "./slice/authSlice";
import { datasetReducer, resetDatasetState } from "./slice/datasetSlice";
import {
  globalFilterReducer,
  resetGlobalFilter
} from "./slice/globalFilterSlice";
import { mapReducer, resetMapState } from "./slice/mapSlice";
import { resetSpatialState, spatialReducer } from "./slice/spatialSlice";
import { resetUIDataConfig, uiReducer } from "./slice/uiSlice";

const mapPersistConfig = {
  key: "map",
  storage: localforage,
  blacklist: ["selectedFeature"]
};

const spatialPersistConfig = {
  key: "spatial",
  storage: localforage,
  blacklist: [
    "catchmentSettingsTemp",
    "adminAreaSettingsDataTemp",
    "adminTreeSettingsTemp"
  ]
};

const datasetPersistConfig = {
  key: "dataset",
  storage: localforage
};

const analysisSummaryPersistConfig = {
  key: "analysisSummary",
  storage: localforage,
  blacklist: ["gridsSelected"]
};

const uiPersistConfig = {
  key: "ui",
  storage: localforage,
  blacklist: [
    "showPolygonPopup",
    "allowPinMarker",
    "showAreaAdminPopup",
    "showCatchmentPopup",
    "showDatasetPopup",
    "showDataWeightSettingsPopup",
    "showSpatialAnalysisResultTabularModal",
    "sideBarMenuTypePopupDisplay",
    "isExpandLegendVisualization",
    "showProjectList",
    "showLegendInfo",
    "sidebarUserSettingPopupDisplay",
    "showSearchLocationPopup",
    "markerInfo",
    "showGlobalFilterPopup",
    "showLoadingOverlay"
  ]
};

const authPersistConfig = {
  key: "auth",
  storage: localforage
};

const analysisJobPersistConfig = {
  key: "analysisJob",
  storage: localforage
};

const globalFilterPersistConfig = {
  key: "globalFilter",
  storage: localforage,
  blacklist: [
    "selectedCountryTemp",
    "selectedAreaIdsTemp",
    "explicitSelectedAreaIdsTemp"
  ]
};

export const rootReducer = combineReducers({
  auth: persistReducer(authPersistConfig, authReducer),
  map: persistReducer(mapPersistConfig, mapReducer),
  dataset: persistReducer(datasetPersistConfig, datasetReducer),
  spatial: persistReducer(spatialPersistConfig, spatialReducer),
  ui: persistReducer(uiPersistConfig, uiReducer),
  analysisSummary: persistReducer(
    analysisSummaryPersistConfig,
    analysisSummaryReducer
  ),
  analysisJob: persistReducer(analysisJobPersistConfig, analysisJobReducer),
  globalFilter: persistReducer(globalFilterPersistConfig, globalFilterReducer)
});

export const store = configureStore({
  devTools: import.meta.env.MODE !== "production",
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Actions from redux-persist are ignored because they may contain non-serializable values
        // and are essential for the persistence mechanism to work correctly.
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    })
});

export const persistor = persistStore(store);

// Function to reset the store
// We can custom the store we want to reset
export const resetStore = async () => {
  store.dispatch(resetAnalysisSummaryState());
  store.dispatch(resetAuth());
  store.dispatch(resetDatasetState());
  store.dispatch(resetMapState());
  store.dispatch(resetSpatialState());
  store.dispatch(resetUIDataConfig());
  store.dispatch(resetAnalysisJob());
  store.dispatch(resetGlobalFilter());
};

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
